import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/common/Button';
import { Loader } from '@/components/common/Loader';
import { RoleGate } from '@/components/common/RoleGate';
import { env } from '@/env';
import { destinazioneIncontroDaLink, tornoFraIMiei } from '@/lib/limulo/incontroDaLink';
import { LIMULO_TOURNAMENTS_URL, limuloTournamentUrl } from '@/lib/limulo/routes';
import { parseStartParam } from '@/lib/startParam';
import { useGetLimuloMatchesQuery, useGetMyLimuloTournamentsConTotaleQuery } from '@/services/limuloApi';

/**
 * `/inc/:tournamentId` — dove atterra chi apre `t.me/<bot>?startapp=inc_<tournamentId>` (il DM «partita pronta» di QBot,
 * I-356, ADR-0194): l'INCONTRO aperto dell'utente in quel torneo, o la scheda del torneo.
 *
 * ═══ PERCHE' UNA SCHERMATA E NON UNA RIGA IN `startParamRoutes` ═══
 * `routeForStartParam` e' pura e sincrona (non guarda la rete), e dove sia l'incontro aperto di QUESTO utente si sa solo
 * leggendo i suoi tornei e i suoi incontri. Questa schermata li legge, decide con `destinazioneIncontroDaLink` (pura, provata)
 * e se ne va con `navigate(…, { replace: true })`: il tasto indietro non deve riportare su un caricamento.
 *
 * ═══ STA FUORI DA `limuloRoutes`, come `/invito/:code` ═══
 * Quelle rotte esistono solo con `VITE_FEATURE_LIMULO_ENABLED` e dentro `<RoleGate role="beta">`: senza il flag o senza il
 * ruolo un link cadrebbe sul catch-all `*` e finirebbe sulla Hub — il fallimento silenzioso che il deep link esiste per
 * togliere. Qui la schermata c'e' sempre e dice quale dei casi e': flag spento → `degraded.switchOff`; senza ruolo → il pannello
 * del `RoleGate`; id che non e' un tid → «non trovato».
 *
 * ═══ IL TID E' UN INPUT NON FIDATO ═══
 * Il `GET …/matches` parte SOLO se il tid e' fra i miei tornei (`GET /v1/tournaments/mine` PRIMA). Per un tid non mio si va
 * alla scheda del torneo come per un URL qualunque, e la pagina del torneo gestisce gia' il non-trovato. Il `matchId` non e'
 * nel link e non viene mai dal link.
 *
 * ═══ GLI ESITI SONO ONESTI ═══
 * Se `/mine` o `/matches` falliscono NON si ripiega in silenzio sul torneo: si dice «non riesco a leggere i tuoi incontri», con
 * «Riprova» e «Vai al torneo». Durante la lettura, il `Loader` della casa.
 *
 * ═══ SOLO DATI LETTI DOPO L'ARRIVO ═══
 * Il cache di RTK Query puo' avere un `/mine` o un `/matches` di prima che l'utente aprisse il link — e il caso tipico di questo
 * link e' proprio un torneo NATO dopo l'ultima lettura (il DM «partita pronta»). Decidere su quello manderebbe alla scheda del
 * torneo chi ha un incontro aperto. Per questo una lettura conta solo se e' stata COMPLETATA dopo il montaggio
 * (`fulfilledTimeStamp >= montataAlle`) e non e' in corso.
 *
 * ⚠️ `/mine` e' limitato a `LIMULO_MINE_LIMIT_MAX` righe (100, ordinate per creazione decrescente): chi ne ha di piu' e non trova
 * il tid fra le prime e' trattato da «non mio» e va alla scheda del torneo, che non e' sbagliata ma e' meno diretta.
 */

/** Una lettura RTK Query e' «arrivata adesso»: completata dopo `daQuando` e non in corso. */
const letta = (q: { isFetching: boolean; isError: boolean; fulfilledTimeStamp?: number }, daQuando: number): boolean =>
  !q.isFetching && !q.isError && q.fulfilledTimeStamp !== undefined && q.fulfilledTimeStamp >= daQuando;

export function IncontroLanding() {
  // Flag spento: la rotta di destinazione non esiste in questa build. Lo si dice, invece di rimbalzare sul catch-all.
  if (!env.featureLimuloEnabled) return <NonAttivo />;
  return (
    <RoleGate role="beta">
      <IncontroLandingBeta />
    </RoleGate>
  );
}

function NonAttivo() {
  const { t } = useTranslation('limulo');
  return (
    <div data-testid="incontro-link-non-attivo" className="flex flex-col gap-2 px-4 pb-6 pt-5">
      <div className="rounded-xl border border-border bg-bg-secondary p-4">
        <h1 className="text-base font-semibold text-fg">{t('degraded.switchOff.title')}</h1>
        <p className="mt-1 text-sm text-fg-muted">{t('degraded.switchOff.body')}</p>
      </div>
    </div>
  );
}

function IncontroLandingBeta() {
  const { t } = useTranslation('limulo');
  const navigate = useNavigate();
  const { tournamentId: daRotta } = useParams<{ tournamentId: string }>();
  const tournamentId = daRotta ?? '';
  // La STESSA grammatica del link (`inc_` + tid, regex chiusa): un path digitato a mano non e' un tid.
  const valido = parseStartParam(`inc_${tournamentId}`)?.kind === 'incontro';

  const [montataAlle] = useState(() => Date.now());
  const mine = useGetMyLimuloTournamentsConTotaleQuery(undefined, {
    skip: !valido,
    refetchOnMountOrArgChange: true,
  });
  const mineLette = valido && letta(mine, montataAlle);
  const fraIMiei = mineLette && tornoFraIMiei(tournamentId, mine.data?.items);

  // Gli incontri si chiedono SOLO per un tid che e' fra i miei (letti adesso).
  const chiediIncontri = fraIMiei;
  const incontri = useGetLimuloMatchesQuery(
    { id: tournamentId },
    { skip: !chiediIncontri, refetchOnMountOrArgChange: true },
  );
  const incontriLetti = chiediIncontri && letta(incontri, montataAlle);

  const decisa = mineLette && (!fraIMiei || incontriLetti);
  const url = decisa
    ? destinazioneIncontroDaLink({ tournamentId, fraIMiei, incontri: incontri.data }).url
    : null;

  useEffect(() => {
    if (url) navigate(url, { replace: true });
  }, [url, navigate]);

  if (!valido) {
    return (
      <div data-testid="incontro-link-non-valido" className="flex flex-col gap-3 px-4 pb-6 pt-5">
        <div className="rounded-xl border border-border bg-bg-secondary p-4">
          <h1 className="text-base font-semibold text-fg">{t('degraded.notFound.title')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('degraded.notFound.body')}</p>
        </div>
        <Button variant="secondary" fullWidth onClick={() => navigate(LIMULO_TOURNAMENTS_URL, { replace: true })}>
          {t('degraded.backToTournaments')}
        </Button>
      </div>
    );
  }

  // `isError` solo se la lettura non e' in corso: un nuovo tentativo deve tornare al caricamento, non lasciare l'errore a schermo.
  const fallita = (!mine.isFetching && mine.isError) || (chiediIncontri && !incontri.isFetching && incontri.isError);
  if (fallita) {
    const riprova = () => {
      if (mine.isError) void mine.refetch();
      else void incontri.refetch();
    };
    return (
      <div data-testid="incontro-link-errore" className="flex flex-col gap-3 px-4 pb-6 pt-5">
        <div className="rounded-xl border border-border bg-bg-secondary p-4">
          <h1 className="text-base font-semibold text-fg">{t('incontroLink.errore.title')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('incontroLink.errore.body')}</p>
        </div>
        <Button fullWidth onClick={riprova}>
          {t('incontroLink.errore.retry')}
        </Button>
        <Button variant="secondary" fullWidth onClick={() => navigate(limuloTournamentUrl(tournamentId), { replace: true })}>
          {t('incontroLink.errore.goToTournament')}
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="incontro-link-carica" className="flex justify-center px-4 py-10">
      <Loader />
    </div>
  );
}
